import { todayISODate } from "../utils/homepageSeason";
import {
  onlineCompanionSinglesNote,
  singlesFollowsDoublesOnline,
} from "./doublesSinglesSetup";
import { onlineSignupsEnabled } from "./signupEnablement";
import {
  isSquadFull,
  signupOccupiesSquad,
  squadSpotCap,
} from "./signupOccupancy";
import {
  timezoneForSquad,
  wallClockInZone,
} from "./signupTimezone";

export const PREVIEW_CLOSE_HOURS_BEFORE_EACH_SQUAD = 24;
export const SELF_SERVE_CANCEL_HOURS_BEFORE_SQUAD = 24;

export const SQUAD_ONLINE = {
  open: "open",
  waitlist: "waitlist",
  full: "full",
  closed: "closed",
  locked: "locked",
};

export const EVENT_ONLINE = {
  selectable: "selectable",
  no_squads: "no_squads",
  closed: "closed",
  full: "full",
  via_doubles: "via_doubles",
};

export const isDoublesEvent = (event) => Number(event?.bowlerQuantity) === 2;
export const isSinglesEvent = (event) => Number(event?.bowlerQuantity) === 1;

export const parseSquadStart = (squad) => {
  if (!squad?.squadDate) return null;
  return wallClockInZone(
    squad.squadDate,
    squad.squadTime,
    timezoneForSquad(squad)
  );
};

export const eventsBySquadFromRows = (rows = []) => {
  const next = {};
  (rows || []).forEach((row) => {
    const squadId = row?.squadID;
    const eventId = row?.eventsID;
    if (!squadId || !eventId) return;
    if (!next[squadId]) next[squadId] = [];
    next[squadId].push(eventId);
  });
  return next;
};

export const selectedEventIdsForSquad = (squad, eventsBySquad = {}) => {
  if (Array.isArray(squad?.eventIds)) return squad.eventIds.filter(Boolean);
  return ((squad?.id && eventsBySquad[squad.id]) || []).filter(Boolean);
};

export const squadHasDoublesAndSingles = (
  squad,
  events = [],
  eventsBySquad = {}
) => {
  const ids = new Set(selectedEventIdsForSquad(squad, eventsBySquad));
  const onSquad = (events || []).filter((event) => ids.has(event.id));
  return onSquad.some(isDoublesEvent) && onSquad.some(isSinglesEvent);
};

export const tournamentHasMixedDoublesSinglesSquad = (
  squads = [],
  events = [],
  eventsBySquad = {}
) =>
  (squads || []).some(
    (squad) =>
      squad &&
      squad.isDeleted !== true &&
      squadHasDoublesAndSingles(squad, events, eventsBySquad)
  );

export const eventsOnSquad = (squad, events = [], eventsBySquad = {}) => {
  const ids = new Set(selectedEventIdsForSquad(squad, eventsBySquad));
  return (events || []).filter((event) => ids.has(event.id));
};

export const resolveSquadEvent = (
  squad,
  events = [],
  eventsBySquad = {},
  currentEvent = null
) => {
  if (!squad) return currentEvent || null;
  const allowed = eventsOnSquad(squad, events, eventsBySquad);
  if (!allowed.length) return null;
  if (currentEvent && allowed.some((event) => event.id === currentEvent.id)) {
    return allowed.find((event) => event.id === currentEvent.id) || currentEvent;
  }
  if (allowed.length === 1) return allowed[0];
  return null;
};

export const eventNamesOnSquad = (
  squad,
  events = [],
  eventsBySquad = {}
) => {
  const ids = selectedEventIdsForSquad(squad, eventsBySquad);
  const byId = new Map((events || []).map((event) => [event.id, event]));
  return ids
    .map((id) => byId.get(id)?.eventName)
    .filter(Boolean);
};

export const squadMaxTeams = (squad, settings, event, options = {}) =>
  squadSpotCap(squad, settings, event, options);

export const effectiveSquadCloseAt = (squad, settings = {}) => {
  if (squad?.signupClosesAt) {
    const override = new Date(squad.signupClosesAt);
    if (!Number.isNaN(override.getTime())) return override;
  }
  const start = parseSquadStart(squad);
  if (!start) return null;
  const hoursRaw = settings.closeHoursBeforeEachSquad;
  if (hoursRaw === undefined || hoursRaw === null || hoursRaw === "") {
    return null;
  }
  const hours = hoursRaw === 0 || hoursRaw === "0" ? 0 : Number(hoursRaw);
  return new Date(start.getTime() - hours * 60 * 60 * 1000);
};

export const tournamentWideCloseAt = (squads = [], settings = {}) => {
  const hours = settings.closeAllHoursBeforeFirstSquad;
  if (hours == null || hours === "") return null;
  const starts = squads
    .filter((squad) => squad && squad.isDeleted !== true)
    .map(parseSquadStart)
    .filter(Boolean);
  if (!starts.length) return null;
  const first = starts.reduce((earliest, next) =>
    next < earliest ? next : earliest
  );
  return new Date(first.getTime() - Number(hours) * 60 * 60 * 1000);
};

export const squadAllowsEvent = (
  squad,
  event,
  { extraEventIds = [], eventsBySquad = {}, extrasBySquad = {} } = {}
) => {
  if (!squad || !event || squad.isDeleted === true) return false;
  const ids = [
    ...selectedEventIdsForSquad(squad, eventsBySquad || extrasBySquad),
    ...(extraEventIds || []),
  ];
  return ids.includes(event.id);
};

export const squadAllowsStandaloneSignupEvent = (
  squad,
  event,
  options = {}
) => {
  if (!squadAllowsEvent(squad, event, options)) return false;
  if (!isSinglesEvent(event)) return true;
  if (!singlesFollowsDoublesOnline(options.doublesSingles)) return true;
  const eventsBySquad = options.eventsBySquad || options.extrasBySquad || {};
  return !squadHasDoublesAndSingles(squad, options.events || [], eventsBySquad);
};

export const squadOnlineStatus = (
  squad,
  {
    now = new Date(),
    occupancy = 0,
    waitlistEnabled = false,
    settings = {},
    squads = [],
    event = null,
    events = [],
    eventsBySquad = {},
    extrasBySquad = {},
    doublesSingles,
  } = {}
) => {
  if (!onlineSignupsEnabled(settings)) return SQUAD_ONLINE.closed;
  if (squad?.signupLocked) return SQUAD_ONLINE.locked;
  const wideClose = tournamentWideCloseAt(squads, settings);
  if (wideClose && now >= wideClose) return SQUAD_ONLINE.closed;
  const closeAt = effectiveSquadCloseAt(squad, settings);
  if (closeAt && now >= closeAt) return SQUAD_ONLINE.closed;
  const bySquad = eventsBySquad || extrasBySquad;
  const occupyOptions = { events, eventsBySquad: bySquad, doublesSingles };
  if (event && !signupOccupiesSquad(event, squad, occupyOptions)) {
    return SQUAD_ONLINE.open;
  }
  const maxSpots = squadSpotCap(squad, settings, event, occupyOptions);
  if (isSquadFull(occupancy, maxSpots)) {
    return waitlistEnabled ? SQUAD_ONLINE.waitlist : SQUAD_ONLINE.full;
  }
  return SQUAD_ONLINE.open;
};

const squadStatusOptions = ({
  now,
  occupancyBySquad = {},
  waitlistEnabled,
  settings,
  squads,
  events,
  eventsBySquad,
  extrasBySquad,
  doublesSingles,
}) => ({
  now,
  occupancyBySquad,
  waitlistEnabled,
  settings,
  squads,
  events,
  eventsBySquad: eventsBySquad || extrasBySquad,
  doublesSingles,
});

const eventStatusFromSquads = (event, compatible, options) => {
  if (!compatible.length) {
    return { status: EVENT_ONLINE.no_squads, reason: "No squads left" };
  }
  const statuses = compatible.map((squad) =>
    squadOnlineStatus(squad, {
      ...squadStatusOptions(options),
      occupancy: options.occupancyBySquad?.[squad.id] || 0,
      event,
    })
  );
  if (
    statuses.some(
      (status) =>
        status === SQUAD_ONLINE.open || status === SQUAD_ONLINE.waitlist
    )
  ) {
    return { status: EVENT_ONLINE.selectable, reason: null };
  }
  if (statuses.every((status) => status === SQUAD_ONLINE.full)) {
    return { status: EVENT_ONLINE.full, reason: "Squads full" };
  }
  return { status: EVENT_ONLINE.closed, reason: "Signup closed" };
};

const companionSinglesStatus = (event, squads = [], options = {}) => {
  if (!isSinglesEvent(event) || !singlesFollowsDoublesOnline(options.doublesSingles)) {
    return null;
  }
  const doublesEvent = (options.events || []).find(isDoublesEvent);
  if (!doublesEvent) return null;
  const doublesCompatible = (squads || []).filter(
    (squad) =>
      squad &&
      squad.isDeleted !== true &&
      squadAllowsEvent(squad, doublesEvent, options)
  );
  const doublesStatus = eventStatusFromSquads(
    doublesEvent,
    doublesCompatible,
    options
  );
  if (doublesStatus.status !== EVENT_ONLINE.selectable) return doublesStatus;
  return {
    status: EVENT_ONLINE.via_doubles,
    reason: onlineCompanionSinglesNote(options.doublesSingles),
  };
};

export const eventOnlineStatus = (
  event,
  squads = [],
  options = {}
) => {
  if (!onlineSignupsEnabled(options.settings)) {
    return { status: EVENT_ONLINE.closed, reason: "Signup closed" };
  }
  const {
    events = [],
    extraEventIds = [],
    eventsBySquad = {},
    extrasBySquad = {},
    doublesSingles,
  } = options;
  const allowOptions = {
    events,
    extraEventIds,
    eventsBySquad: eventsBySquad || extrasBySquad,
    doublesSingles,
  };
  const compatible = (squads || []).filter(
    (squad) =>
      squad &&
      squad.isDeleted !== true &&
      squadAllowsStandaloneSignupEvent(squad, event, allowOptions)
  );
  const standalone = eventStatusFromSquads(event, compatible, options);
  if (standalone.status === EVENT_ONLINE.selectable) return standalone;

  const companion = companionSinglesStatus(event, squads, options);
  if (companion?.status === EVENT_ONLINE.via_doubles) return companion;
  if (standalone.status !== EVENT_ONLINE.no_squads) return standalone;
  if (companion) return companion;
  return standalone;
};

export const shouldListSignupEvent = (event, squads = [], options = {}) => {
  const status = eventOnlineStatus(event, squads, options);
  if (status.status === EVENT_ONLINE.via_doubles) return true;
  if (isSinglesEvent(event) && status.status === EVENT_ONLINE.no_squads) {
    return false;
  }
  return true;
};

export const compatibleSquadsForEvent = (event, squads = [], options = {}) =>
  (squads || []).filter(
    (squad) =>
      squad &&
      squad.isDeleted !== true &&
      squadAllowsStandaloneSignupEvent(squad, event, options)
  );

export const squadStatusLabel = (status) => {
  if (status === SQUAD_ONLINE.locked) return "Locked";
  if (status === SQUAD_ONLINE.closed) return "Signup closed";
  if (status === SQUAD_ONLINE.full) return "This squad is full";
  if (status === SQUAD_ONLINE.waitlist) return "Join waitlist";
  return null;
};

export const onlineSignupHasOpenPath = (events = [], squads = [], options = {}) => {
  if (!onlineSignupsEnabled(options.settings)) return false;
  return (events || []).some((event) => {
    const status = eventOnlineStatus(event, squads, options);
    return status.status === EVENT_ONLINE.selectable;
  });
};

export const SIGNUP_BANNER = {
  open: "open",
  closed: "closed",
  none: "none",
};

/** Last squad this many days before today counts as Done. */
export const SIGNUP_DONE_DAYS_AFTER_LAST_SQUAD = 30;

const shiftISODate = (isoDate, days) => {
  const [year, month, day] = String(isoDate).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const latestLiveSquadDate = (squads = []) => {
  const dates = (squads || [])
    .filter((squad) => squad && squad.isDeleted !== true && squad.squadDate)
    .map((squad) => String(squad.squadDate).slice(0, 10))
    .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
    .sort();
  return dates.length ? dates[dates.length - 1] : null;
};

/** Lock Results, or the last squad date is more than 30 days before today. */
export const signupTournamentIsDone = ({
  resultsVerified = false,
  squads = [],
  today = todayISODate(),
} = {}) => {
  if (resultsVerified) return true;
  const last = latestLiveSquadDate(squads);
  if (!last) return false;
  const cutoff = shiftISODate(today, -SIGNUP_DONE_DAYS_AFTER_LAST_SQUAD);
  return Boolean(cutoff && last < cutoff);
};

/**
 * Overview banner. Done and allowSignups false render nothing.
 * Unknown occupancy does not count as an open squad.
 */
export const signupBannerState = ({
  resultsVerified = false,
  squads = [],
  settings,
  hasOpenPath = false,
  occupancyKnown = true,
  today = todayISODate(),
} = {}) => {
  if (signupTournamentIsDone({ resultsVerified, squads, today })) {
    return SIGNUP_BANNER.none;
  }
  if (settings?.allowSignups === false) {
    return SIGNUP_BANNER.none;
  }
  if (!occupancyKnown) {
    return SIGNUP_BANNER.closed;
  }
  return hasOpenPath ? SIGNUP_BANNER.open : SIGNUP_BANNER.closed;
};

export const selfServeCancelCutoffAt = (squad) => {
  const start = parseSquadStart(squad);
  if (!start) return null;
  return new Date(
    start.getTime() - SELF_SERVE_CANCEL_HOURS_BEFORE_SQUAD * 60 * 60 * 1000
  );
};

export const canSelfServeCancel = (signup, now = new Date()) => {
  const status = signup?.status;
  if (status === "waitlisted") return true;
  if (!["pending", "approved", "held"].includes(status)) return false;
  const cutoff = selfServeCancelCutoffAt(signup?.squad);
  if (!cutoff) return true;
  return now < cutoff;
};

export const requiredCancelGroupIdOf = (signup) =>
  signup?.requiredCancelGroupId || null;

/** Doubles line, or any line that is not in a required group. */
export const isRequiredCancelAnchor = (signup) => {
  const groupId = requiredCancelGroupIdOf(signup);
  if (!groupId) return true;
  return signup?.id === groupId;
};

export const requiredCancelMembers = (signup, signups = []) => {
  const groupId = requiredCancelGroupIdOf(signup);
  if (!groupId) return signup ? [signup] : [];
  const members = (signups || []).filter(
    (row) => row && requiredCancelGroupIdOf(row) === groupId
  );
  return members.length ? members : signup ? [signup] : [];
};

/** One cancel for a required group. Hidden on companion lines. Blocked when any line is past the deadline. */
export const canSelfServeCancelEntry = (
  signup,
  signups = [],
  now = new Date()
) => {
  if (!signup || !isRequiredCancelAnchor(signup)) return false;
  return requiredCancelMembers(signup, signups).every((row) =>
    canSelfServeCancel(row, now)
  );
};

const entryEventName = (signup) =>
  signup?.tournamentEvents?.eventName || signup?.event?.eventName || "";

const entryBowlerNames = (signup) => {
  const fromSignup = (signup?.signupBowlers || [])
    .map((bowler) => bowler?.name)
    .filter(Boolean);
  if (fromSignup.length) return fromSignup;
  return [signup?.primaryBowler, ...(signup?.teammates || [])]
    .filter(Boolean)
    .map((bowler) => bowler.name)
    .filter(Boolean);
};

export const requiredCancelEntryLabel = (signup) => {
  const eventName = entryEventName(signup) || "Entry";
  const names = entryBowlerNames(signup);
  return names.length ? `${eventName} (${names.join(", ")})` : eventName;
};

const joinEntryLabels = (labels) => {
  if (labels.length <= 1) return labels[0] || "this signup";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
};

export const requiredCancelCompanionNote = (
  signup,
  signups = [],
  now = new Date()
) => {
  if (!signup || isRequiredCancelAnchor(signup)) return "";
  const anchor = (signups || []).find(
    (row) => row?.id && row.id === requiredCancelGroupIdOf(signup)
  );
  if (!anchor) return "";
  const eventName = entryEventName(anchor) || "Doubles";
  if (canSelfServeCancelEntry(anchor, signups, now)) {
    return `Cancels with ${eventName}.`;
  }
  if (
    requiredCancelMembers(anchor, signups).some((row) =>
      canRequestRefund(row, now)
    )
  ) {
    return `Request a refund with ${eventName}.`;
  }
  return "";
};

export const requiredCancelConfirmCopy = (signup, signups = []) => {
  const members = requiredCancelMembers(signup, signups);
  const ordered = [...members].sort((left, right) => {
    const leftAnchor = isRequiredCancelAnchor(left) ? 0 : 1;
    const rightAnchor = isRequiredCancelAnchor(right) ? 0 : 1;
    return leftAnchor - rightAnchor;
  });
  if (ordered.length < 2) {
    return {
      title: "Cancel signup",
      message:
        "This removes the signup from the squad. You cannot undo it. If you paid with Pay now, the advertised entry and the 2% platform fee go back on the card. Card processing is not refunded.",
      confirmText: "Cancel signup",
    };
  }
  const list = joinEntryLabels(ordered.map(requiredCancelEntryLabel));
  return {
    title: "Cancel these entries",
    message: `This removes ${list} from the squad. You cannot undo it. If you paid with Pay now, the advertised entry and the 2% platform fee for these entries go back on the card. Card processing is not refunded.`,
    confirmText: "Cancel these entries",
  };
};

export const requiredCancelNeedsCardRefund = (signup, signups = []) =>
  requiredCancelMembers(signup, signups).some(
    (row) => row?.paymentStatus === "paid"
  );

/** One refund request for a required group. Hidden on companion lines. */
export const canRequestRefundEntry = (signup, signups = [], now = new Date()) => {
  if (!signup || !isRequiredCancelAnchor(signup)) return false;
  return requiredCancelMembers(signup, signups).some((row) =>
    canRequestRefund(row, now)
  );
};

export const requiredRefundRequestCopy = (signup, signups = []) => {
  const members = requiredCancelMembers(signup, signups).filter((row) =>
    canRequestRefund(row)
  );
  if (members.length < 2) {
    return {
      title: "Request a refund",
      message:
        "The director will see this on Signup Dashboard. They refund from Signup Review.",
      confirmText: "Request refund",
    };
  }
  const ordered = [...members].sort((left, right) => {
    const leftAnchor = isRequiredCancelAnchor(left) ? 0 : 1;
    const rightAnchor = isRequiredCancelAnchor(right) ? 0 : 1;
    return leftAnchor - rightAnchor;
  });
  const list = joinEntryLabels(ordered.map(requiredCancelEntryLabel));
  return {
    title: "Request a refund",
    message: `The director will see this on Signup Dashboard for ${list}. They refund from Signup Review.`,
    confirmText: "Request refund",
  };
};

/** Bowler Cancel frees the spot. Director Refund and stay leaves them on squad. */
export const selfServeCancelResult = (signup = {}) => {
  const paid =
    signup.paymentStatus === "paid" || signup.paymentStatus === "walk_up";
  return {
    status: "cancelled",
    paymentStatus: paid ? "refunded" : signup.paymentStatus || "unpaid",
    stayOnSquad: false,
  };
};

export const canRequestRefund = (signup, now = new Date()) => {
  if (signup?.paymentStatus !== "paid") return false;
  if (!["pending", "approved", "held"].includes(signup?.status)) return false;
  if (signup?.refundRequested || signup?.refundRequestStatus === "open") {
    return false;
  }
  return !canSelfServeCancel(signup, now);
};
